import React, { useState } from 'react';
import { Button } from 'equasion-lib';
import { FiCheckCircle } from 'react-icons/fi';
import ZoneSelectWithScan from '@/lib/shared-coms/ZoneSelectWithScan';
import {
  manualPickVerifyUnit,
  normalizeExpirationInput,
  type PickVerifyUnitInput
} from '@/lib/scanning/retailCode';
import {
  parseDamagedQuantity,
  pickDamagedUnitLabel,
  type PickLine,
  type PickQuarantineReason,
  type PickQuarantineState,
  type PickZone
} from '../../pickLineSteps';

type PickQuarantinePanelProps = {
  line: PickLine;
  companyId: string;
  quarantine: PickQuarantineState;
  /** A lookup / the quarantine itself is in flight. */
  busy: boolean;
  onChoose: (mode: PickQuarantineReason) => void;
  onBack: () => void;
  onClose: () => void;
  onQuarantineZoneChosen: (zone: PickZone | null) => void;
  /** Whether the zone section is waiting for the sheet's scanner to read a label. */
  onQuarantineZoneScanning: (scanning: boolean) => void;
  onDamagedUnitTyped: (unit: PickVerifyUnitInput) => void;
  onSubmitMissing: () => void;
  onSubmitDamaged: (quantity: number | null) => void;
};

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

const QUARANTINE_SUFFIX = ' (Quarantine)';

/**
 * The Pick sheet's "Quarantine", in place of the step prompt so the camera below stays live: it reads
 * the quarantine zone's label (Missing) or the damaged unit (Damaged), routed here by the sheet.
 *
 *  - Missing: the shelf holds none of the product its records say this zone does. EVERY unit of it
 *    recorded in the zone goes to the quarantine zone the picker scans or chooses (same location —
 *    the list is scoped to it), quarantined; the line then moves on to its next zone.
 *  - Damaged: the scanned (or typed) unit is quarantined where it is — the stock zone keeps it,
 *    quarantined. A serialized line's unit is its 2D barcode (the serial is what is quarantined);
 *    a BULK / UPC line takes a quantity.
 *
 * Nothing is written until the explicit button: a scan only fills the panel in.
 */
function PickQuarantinePanel({
  line,
  companyId,
  quarantine,
  busy,
  onChoose,
  onBack,
  onClose,
  onQuarantineZoneChosen,
  onQuarantineZoneScanning,
  onDamagedUnitTyped,
  onSubmitMissing,
  onSubmitDamaged
}: PickQuarantinePanelProps) {
  const { mode, zone, quarantineZone, unit } = quarantine;
  const serialized = line.kind === 'SERIALIZED';
  const [quantityText, setQuantityText] = useState('1');
  const [typing, setTyping] = useState(false);
  const [code, setCode] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [lot, setLot] = useState('');
  const [expiration, setExpiration] = useState('');

  if (mode === 'choose') {
    return (
      <div className="pick-quarantine" role="group" aria-label="Quarantine">
        <h2>
          Quarantine <strong>{line.productName}</strong>
        </h2>
        <p>At {zone.name}. What is wrong?</p>
        <button
          type="button"
          className="pick-quarantine__choice"
          onClick={() => onChoose('missing')}
        >
          <strong>Missing</strong>
          <span>
            None of it is in {zone.name}. Move every unit recorded there to a quarantine
            zone.
          </span>
        </button>
        <button
          type="button"
          className="pick-quarantine__choice"
          onClick={() => onChoose('damaged')}
        >
          <strong>Damaged</strong>
          <span>Quarantine a damaged unit. It stays in {zone.name}.</span>
        </button>
        <Button type="secondary" fullWidth {...click(onClose)}>
          Cancel
        </Button>
      </div>
    );
  }

  if (mode === 'missing') {
    return (
      <div className="pick-quarantine" role="group" aria-label="Quarantine missing stock">
        <h2>
          Missing from <strong>{zone.name}</strong>
        </h2>
        <p>
          Every unit of {line.productName} recorded in {zone.name} goes to the quarantine
          zone, quarantined. Scan the quarantine zone&apos;s label, or choose it.
        </p>
        {/* The sheet's scanner below is already live and reads this label (the device runs one
            scanner at a time), so the zone section says so rather than starting its own. */}
        <ZoneSelectWithScan
          form={false}
          liveScanPrompt="Scan the quarantine zone's label with the scanner below."
          onScanningChange={onQuarantineZoneScanning}
          label="Quarantine zone"
          companyId={companyId}
          locationId={zone.locationId || undefined}
          zoneTypes={['quarantine']}
          value={quarantineZone?.id ?? ''}
          valueDisplay={quarantineZone?.name}
          onChange={(selection) =>
            onQuarantineZoneChosen(
              selection?.value
                ? {
                    id: selection.value,
                    name: selection.display.endsWith(QUARANTINE_SUFFIX)
                      ? selection.display.slice(0, -QUARANTINE_SUFFIX.length)
                      : selection.display,
                    zoneType: 'quarantine'
                  }
                : null
            )
          }
        />
        {quarantineZone && (
          <p className="pick-quarantine__picked">
            <FiCheckCircle aria-hidden /> Going to {quarantineZone.name}
          </p>
        )}
        <Button
          type="primary"
          fullWidth
          disabled={busy || !quarantineZone}
          {...click(onSubmitMissing)}
        >
          {busy ? 'Quarantining…' : `Quarantine all of it from ${zone.name}`}
        </Button>
        <Button type="secondary" fullWidth disabled={busy} {...click(onBack)}>
          Back
        </Button>
      </div>
    );
  }

  const quantity = serialized ? null : parseDamagedQuantity(quantityText);
  const normalizedExpiration = normalizeExpirationInput(expiration);
  const typedReady = serialized
    ? !!code.trim() && !!serialNumber.trim() && !!normalizedExpiration
    : !!code.trim();
  const useTyped = () => {
    if (!typedReady) return;
    const typed = manualPickVerifyUnit({
      code,
      gtin: serialized ? code : null,
      serialNumber: serialized ? serialNumber : null,
      expiration: serialized ? expiration : null
    });
    onDamagedUnitTyped(serialized ? typed : { ...typed, lot: lot.trim() || null });
    setTyping(false);
  };

  return (
    <div className="pick-quarantine" role="group" aria-label="Quarantine damaged stock">
      <h2>
        Damaged <strong>{line.productName}</strong>
      </h2>
      <p>
        {serialized
          ? 'Scan the 2D barcode on the damaged unit.'
          : 'Scan the barcode on the damaged pack.'}{' '}
        It stays in {zone.name}, quarantined.
      </p>
      {unit && (
        <p className="pick-quarantine__picked">
          <FiCheckCircle aria-hidden /> {pickDamagedUnitLabel(unit)}
        </p>
      )}
      {!serialized && (
        <label className="pick-manual__field">
          <span>Damaged quantity</span>
          <input
            name="damagedQuantity"
            inputMode="numeric"
            autoComplete="off"
            value={quantityText}
            onChange={(e) => setQuantityText(e.target.value)}
          />
        </label>
      )}
      {typing ? (
        <div className="pick-quarantine__typed">
          <label className="pick-manual__field">
            <span>{serialized ? 'GTIN' : 'NDC / UPC / GTIN'}</span>
            <input
              name="damagedCode"
              autoFocus
              autoComplete="off"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          {serialized ? (
            <>
              <label className="pick-manual__field">
                <span>Serial number</span>
                <input
                  name="damagedSerialNumber"
                  autoComplete="off"
                  autoCapitalize="characters"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                />
              </label>
              <label className="pick-manual__field">
                <span>Expiration date</span>
                <input
                  name="damagedExpiration"
                  type="date"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
              </label>
            </>
          ) : (
            <label className="pick-manual__field">
              <span>Lot (optional)</span>
              <input
                name="damagedLot"
                autoComplete="off"
                autoCapitalize="characters"
                value={lot}
                onChange={(e) => setLot(e.target.value)}
              />
            </label>
          )}
          <Button type="secondary" fullWidth disabled={!typedReady} {...click(useTyped)}>
            Use this unit
          </Button>
        </div>
      ) : (
        <button
          type="button"
          className="pick-quarantine__type"
          disabled={busy}
          onClick={() => setTyping(true)}
        >
          Label will not scan? Type it
        </button>
      )}
      <Button
        type="primary"
        fullWidth
        disabled={busy || !unit || (!serialized && quantity === null)}
        {...click(() => onSubmitDamaged(quantity))}
      >
        {busy
          ? 'Quarantining…'
          : serialized
            ? 'Quarantine damaged unit'
            : `Quarantine ${quantity ?? ''} damaged`.trim()}
      </Button>
      <Button type="secondary" fullWidth disabled={busy} {...click(onBack)}>
        Back
      </Button>
    </div>
  );
}

export default PickQuarantinePanel;
